'use client';
import type { AxiosError } from '@cxnpl/api/axios';
import { getErrorMessage, isAxiosError } from '@cxnpl/api/axios';
import { useLoginUser } from '@cxnpl/api/users/users';
import { Text, YStack } from '@cxnpl/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'solito/navigation';
import { useRouter } from '@cxnpl/router';
import type { BRAND } from 'zod';
import { useAppDispatch, useAppSelector } from 'app/store';
import { setInactivityLogout, setCustomerNumber } from '../authSlice';
import { MfaForm, type MfaFormProps, LoginForm, type LoginFormProps } from './components';

export function LoginPage() {
  const { t } = useTranslation();

  const router = useRouter();
  const params = useSearchParams();
  const dispatch = useAppDispatch();
  const showInactivityLogout = useAppSelector((state) => state.auth.inactivityLogout);

  const login = useLoginUser();

  const [showMFAForm, setShowMFAForm] = useState<boolean>(false);
  const [tempUsername, setTempUsername] = useState<string>(''); // Save username temporarily for login after mfa verification
  const [tempPassword, setTempPassword] = useState<string>(''); // Save password temporarily for login after mfa verification
  const [mfaChallengeId, setMfaChallengeId] = useState<string>('');
  const [loginServerError, setLoginServerError] = useState<string | undefined>(undefined);

  //WEB-ONLY: Prefetch other static routes
  useEffect(() => {
    router.prefetch('/help');
    router.prefetch('/signup');
  }, [router]);

  const deviceInfo = {
    deviceId: '',
    deviceType: 'WEB',
    webFields: {
      model: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    },
  } as const;

  const onDismissError = () => {
    setLoginServerError(undefined);
  };

  const onSubmitLogin: LoginFormProps['onSubmit'] = async ({ customerNumber, password }, loginForm) => {
    try {
      // Set customer number
      dispatch(setCustomerNumber(customerNumber));

      const response = await login.mutateAsync({
        data: {
          username: customerNumber,
          password,
          loginAlias: 'customerNumber',
          deviceInfo,
          appClient: 'WEB',
        },
      });

      // successful login ( shouldnt be possible, but just in case)
      if ('result' in response && response.result === 'success') {
        handleLoginResponse();
        return;
      }

      // MFA is required
      if ('result' in response && 'mfaChallengeId' in response) {
        setTempUsername(customerNumber);
        setTempPassword(password);
        setShowMFAForm(true);
        setMfaChallengeId(response.mfaChallengeId);
      }
    } catch (e) {
      if (isAxiosError(e)) {
        const error: AxiosError = e as AxiosError;
        /**
         * Error handling logic:
         *
         * if error code is 500 or more, an outage or server glitch is assumed: show an alert on top of the form
         * (in this case the form is small, so error will always be visible. Longer forms should also account for scrolling to the error alert)
         * if error code is in the 400s, wrong username or password: show field errors
         * otherwise, unknown cases, also show alert on top
         *
         */
        if (error.response && error.response.status >= 500) {
          setLoginServerError(t('auth.components.loginForm.loginServerError'));
        } else if (error.response && error.response.status >= 400) {
          // login will provide no signalling, so incorrect customer number or password will always ask for MFA
          // If login somehow fails, show an error just in case
          loginForm.setError('customerNumber', {
            message: t('auth.components.loginForm.invalidCredentialsErrorMessage'),
            type: 'value',
          });
          loginForm.setError('password', {
            message: t('auth.components.loginForm.invalidCredentialsErrorMessage'),
            type: 'value',
          });
        } else {
          setLoginServerError(t('auth.components.loginForm.loginServerError'));
        }
      } else {
        //error is not an axios error, this should not happen, show error alert just in case
        setLoginServerError(t('auth.components.loginForm.loginServerError'));
      }
    }
  };

  const onPressNeedHelp = () => {
    router.push('/help');
  };

  const onPressSignUp = () => {
    router.push('/signup');
  };

  const onSubmitMFA: MfaFormProps['onSubmit'] = async ({ code }, mfaForm) => {
    try {
      const response = await login.mutateAsync({
        data: {
          username: tempUsername,
          password: tempPassword,
          loginAlias: 'customerNumber',
          mfaChallengeId,
          mfaCode: code,
          deviceInfo,
          appClient: 'WEB',
        },
      });

      if ('result' in response && response.result === 'success') {
        handleLoginResponse();
      }
    } catch (e) {
      // MFA unsuccessful
      mfaForm.setValue('code', '' as string & BRAND<'passwordCode'>);
      mfaForm.setError('code', {
        message: getErrorMessage(e),
        type: 'value',
      });
    }
  };

  const handleLoginResponse = () => {
    dispatch(setInactivityLogout(false));

    const redirectParams = params?.get('redirect');
    const redirectUri = redirectParams && decodeURIComponent(redirectParams);
    router.push(redirectUri || '/home');
  };

  const onResend: MfaFormProps['onResend'] = async (mfaForm) => {
    try {
      const response = await login.mutateAsync({
        data: {
          username: tempUsername,
          password: tempPassword,
          loginAlias: 'customerNumber',
          appClient: 'WEB',
          deviceInfo,
        },
      });
      if ('result' in response && 'mfaChallengeId' in response) {
        setMfaChallengeId(response.mfaChallengeId);
      }
    } catch (e) {
      mfaForm.setError('code', {
        message: 'Failed to resend security code',
        type: 'value',
      });
    }
  };

  return (
    <YStack
      width="100%"
      backgroundColor="$background/surface"
      padding="$space.xl"
      borderRadius="$surface/radius/surface-radius"
      gap="$space.2xl"
      $tablet={{
        maxWidth: 536,
        padding: '$space.8xl',
      }}
      $laptop={{
        maxWidth: 468,
        padding: '$space.5xl',
      }}
    >
      {showMFAForm ? (
        <Text
          variant="bodyMediumEm"
          link
          color="$button/color/button-primary-text"
          onPress={() => {
            setTempUsername('');
            setTempPassword('');
            setShowMFAForm(false);
          }}
        >
          {t('auth.loginPage.backLink')}
        </Text>
      ) : null}
      {!showMFAForm ? (
        <LoginForm
          onSubmit={onSubmitLogin}
          onPressNeedHelp={onPressNeedHelp}
          onPressSignUp={onPressSignUp}
          showInactivityLogout={showInactivityLogout}
          loginServerError={loginServerError}
          onDismissError={onDismissError}
        />
      ) : (
        <MfaForm onSubmit={onSubmitMFA} onResend={onResend} />
      )}
    </YStack>
  );
}
